import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Result, Typography, Button,
} from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import { micrositioService, fixUrl } from '../../services/api';
import { SiteFooter, SiteHeader, colapsarAgrupados, heroBackground, heroTextLayout, heroTextPadding } from './RematePage';
import PujaSpinner from '../../components/PujaSpinner';
import AuthContext from '../../context/AuthContext';
import { TIPOGRAFIAS, altoHeaderDe } from '../../utils/micrositioTema';
import { heroMargenSuperior } from '../../utils/hero';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import { urlSegura } from '../../utils/urlSegura';
import { htmlSanitizado } from '../../utils/htmlSeguro';

const { Title, Text } = Typography;

// tipo: 'comercial' | 'sanitaria' | 'reclamos'
const CondicionesPage = ({ tipo }) => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el
  // iframe en vez de dejarlo clavado en los 800px del snippet.
  useAvisarAltoAlPadre();

  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await micrositioService.getBySlug(slug);
        setData(res.data);
      } catch (err) {
        // Sin `message.error` a proposito: esto no es una accion puntual que
        // falla, es la carga entera de la pantalla. El error se comunica con el
        // <Result> a pagina completa de mas abajo, que dice mas que un toast
        // que se va en tres segundos. Un toast ADEMAS seria ruido duplicado.
        setError(err.response?.status === 404 ? 'not_found' : 'server_error');
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [slug]);

  if (loading) {
    return <PujaSpinner size="lg" label="Cargando…" fondo="#fff" />;
  }

  if (error === 'not_found') {
    return <Result status="404" title="Remate no encontrado" />;
  }

  if (error) {
    return <Result status="500" title="Error al cargar" />;
  }

  const { evento, categorias, condiciones } = data;
  const config = evento.configuracion_micrositio || {};
  const tipografia = TIPOGRAFIAS[config.tipografia_combo] || TIPOGRAFIAS.moderna;
  const tipoCabecera = config.cabecera || 'cabecera-1';
  // `altoHeaderDe` y no la tabla a mano: el panel calcula con el el margen
  // automatico que le MUESTRA al productor, y dos cuentas del mismo numero es
  // justo la divergencia que `micrositioTema.js` existe para matar.
  const headerHeight = altoHeaderDe(config);
  const isTwoRow = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  // Hero desactivado en páginas internas → sin banner, header sólido arriba.
  const heroOff = !!config.hero_off_internas;
  const colorHeaderInitial = config.color_header_initial || 'transparent';
  const heroTransparente = !heroOff && !isTwoRow && colorHeaderInitial === 'transparent';
  const heroMarginTop = heroMargenSuperior(config, headerHeight, heroTransparente);
  const heroPaddingTop = heroTransparente ? headerHeight : 0;
  // Config para el header: si el hero está off, forzamos fondo sólido.
  const configHeader = heroOff
    ? { ...config, color_header_initial: config.color_header || config.color_primary || '#1a5f2a' }
    : config;

  const cssVars = {
    '--color-primary': config.color_primary || '#1a5f2a',
    '--color-secondary': config.color_secondary || '#8B4513',
  };

  // Las consume `.micrositio-page .hero-zone` en index.css, que ademas les
  // pone el tope de mobile. Aca solo viaja el dato, que es lo unico que cambia
  // entre paginas.
  const heroVars = {
    '--hero-margin-top':  `${heroMarginTop}px`,
    '--hero-padding-top': `${heroPaddingTop}px`,
  };

  // Seleccionar sección según tipo
  const seccion = {
    comercial: {
      titulo: condiciones?.titulo_comercial || 'Condiciones Comerciales',
      subtitulo: condiciones?.subtitulo_comercial,
      contenido: condiciones?.condiciones_comerciales,
      garantia: null,
    },
    sanitaria: {
      titulo: condiciones?.titulo_sanitaria || 'Condiciones Sanitarias',
      subtitulo: condiciones?.subtitulo_sanitaria,
      contenido: condiciones?.condiciones_sanitarias,
      garantia: null,
    },
    reclamos: {
      titulo: condiciones?.titulo_reclamos || 'Reclamos y Garantías',
      subtitulo: condiciones?.subtitulo_reclamos,
      contenido: condiciones?.reclamos_garantias,
      garantia: condiciones?.garantia,
    },
  }[tipo];

  // Se deriva del listado COLAPSADO, igual que el catálogo. Sobre el crudo, un
  // lote agrupado con animales de dos cabañas metía en el menú una cabaña que
  // el catálogo no muestra: al clickearla el visitante caía en una grilla
  // vacía. La lista tiene que decir lo que el catálogo puede filtrar.
  const animalesMenu = colapsarAgrupados(data.animales || []);
  const razas = [...new Set(
    animalesMenu.filter(a => a.nom_raza).map(a => a.nom_raza)
  )].sort();
  // Vacío en un remate de una sola cabaña, y ahí el menú no se dibuja.
  const cabanas = [...new Set(
    animalesMenu.filter(a => a.nom_cabana).map(a => a.nom_cabana)
  )].sort();

  return (
    <div
      className="micrositio-page"
      style={{ fontFamily: tipografia.body, minHeight: '100vh', ...cssVars, ...heroVars }}
    >
      <SiteHeader
        config={configHeader}
        tipografia={tipografia}
        logoUrl={evento.img_evento}
        evento={evento}
        categorias={categorias}
        razas={razas}
        cabanas={cabanas}
        scrolled={scrolled}
        slug={slug}
        isAuthenticated={isAuthenticated}
      />

      {/* ── HERO (oculto si hero_off_internas) ── */}
      {heroOff ? (
        <div className="hero-off-spacer" style={{ '--hero-off-header': `${headerHeight}px` }} />
      ) : (
        <>
          <div
            className="hero-zone"
            style={{
              background: heroBackground(config, evento.imgsec_evento ? fixUrl(evento.imgsec_evento) : null),
              minHeight: config.hero_altura || (heroTransparente ? 260 : 180),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              // column: el eje vertical lo maneja justifyContent, no alignItems.
              ...heroTextLayout(config, 'column'),
            }}
          >
            {/* Titulo y subtitulo envueltos: el ajuste de separacion despega
                el BLOQUE del borde del hero. Aplicado al <Title> suelto
                separaba el titulo del subtitulo. Ver ContactoPage. */}
            <div style={{ textAlign: 'center', paddingLeft: 24, paddingRight: 24, ...heroTextPadding(config) }}>
              <Title
                level={1}
                style={{
                  color: config.color_hero_text || '#ffffff',
                  fontFamily: tipografia.title,
                  fontSize: config.hero_text_size || 32,
                  margin: 0,
                  textTransform: config.text_transform_hero || 'none',
                }}
              >
                {evento.nombre_evento}
              </Title>
              {/* El titulo de la seccion NO se repite acá: los productores lo
                  cargan ya con el nombre del remate adentro ("CONDICIONES
                  SANITARIAS ESPECIAL DEL NORTE 2026"), asi que debajo del
                  <Title> quedaba el nombre del evento dos veces. El titulo se
                  muestra una sola vez, al abrir el contenido. */}
            </div>
          </div>

          {/* ── DIVIDER ── */}
          <div style={{ height: 5, background: config.color_primary || '#1a5f2a' }} />
        </>
      )}

      {/* ── CONTENIDO ── */}
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '64px 24px 80px' }}>

        <Title
          level={2}
          style={{
            fontFamily: tipografia.title,
            color: config.color_title || '#1a1a1a',
            marginBottom: seccion.subtitulo ? 8 : 32,
          }}
        >
          {seccion.titulo}
        </Title>

        {seccion.subtitulo && (
          <Text
            style={{
              fontFamily: tipografia.body,
              fontSize: 16,
              color: config.color_paragraph || '#666666',
              display: 'block',
              marginBottom: 32,
            }}
          >
            {seccion.subtitulo}
          </Text>
        )}

        {seccion.contenido ? (
          <div
            style={{ fontFamily: tipografia.body, color: config.color_paragraph || '#444', lineHeight: 1.8, fontSize: 15 }}
            {...htmlSanitizado(seccion.contenido)}
          />
        ) : (
          <Text style={{ color: '#999', fontFamily: tipografia.body }}>
            No hay información disponible para esta sección.
          </Text>
        )}

        {seccion.garantia && (
          <div style={{ marginTop: 40 }}>
            <Button
              type="primary"
              icon={<FilePdfOutlined />}
              // Misma guarda que en la ficha del animal: `fixUrl` deja pasar
              // los `data:` verbatim porque los necesita para el `src` de las
              // imagenes, y este es un `<a href>` publico.
              href={urlSegura(seccion.garantia) ? fixUrl(seccion.garantia) : undefined}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: config.color_btn_primary || config.color_primary || '#1a5f2a',
                border: 'none',
                borderRadius: 32,
                padding: '10px 24px',
                height: 'auto',
                fontSize: 14,
              }}
            >
              Descargar garantía (PDF)
            </Button>
          </div>
        )}
      </div>

      <SiteFooter
        config={config}
        tipografia={tipografia}
        evento={evento}
        categorias={categorias}
        logoUrl={evento.img_evento}
        onCategoriaSelect={(id) => navigate(`/remate/${slug}`, { state: { categoriaFiltro: id } })}
        slug={slug}
      />
    </div>
  );
};


export default CondicionesPage;
